import React from 'react'
import { useUIStore } from '@/store/ui.store'

interface LogoProps {
  className?: string
  alt?: string
  style?: React.CSSProperties
}

/**
 * Get logo path based on current theme
 * Can be used in both React components and non-React contexts (like print pages)
 * Uses erp.png for light mode and erplight.png for dark mode
 */
export const getLogoPath = (): string => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return '/images/logos/erp.png'
  }

  // Get theme from localStorage or check system preference
  const storedTheme = localStorage.getItem('ui-theme') as 'light' | 'dark' | null
  
  let actualTheme: 'light' | 'dark' = 'light'
  
  if (storedTheme === 'dark') {
    actualTheme = 'dark'
  } else if (storedTheme === 'light') {
    actualTheme = 'light'
  } else {
    // If no saved theme, check system preference
    actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  // Use erp.png for light mode, erplight.png for dark mode
  return actualTheme === 'light' 
    ? '/images/logos/erp.png'        // Light mode logo
    : '/images/logos/erplight.png'   // Dark mode logo
}

/**
 * Logo component that switches between light and dark mode logos
 * Shows erp.png in light mode, erplight.png in dark mode
 */
export const Logo: React.FC<LogoProps> = ({ 
  className = '', 
  alt = 'Logo',
  style 
}) => {
  const { theme } = useUIStore()
  
  // Function to get current theme from document
  const getCurrentTheme = (): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light'
    const root = document.documentElement
    if (root.classList.contains('dark')) return 'dark'
    if (root.classList.contains('light')) return 'light'
    // If no class, check store or localStorage
    if (theme === 'dark' || theme === 'light') return theme
    const stored = localStorage.getItem('ui-theme')
    if (stored === 'dark' || stored === 'light') return stored as 'light' | 'dark'
    // Default to light
    return 'light'
  }
  
  const [actualTheme, setActualTheme] = React.useState<'light' | 'dark'>(getCurrentTheme)
  
  // Update when theme changes - check document class directly
  React.useEffect(() => {
    setActualTheme(getCurrentTheme())
  }, [theme])
  
  // Listen for class changes on document element
  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      setActualTheme(getCurrentTheme())
    })
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })
    
    return () => observer.disconnect()
  }, [theme])

  // Use erp.png for light mode, erplight.png for dark mode
  const logoPath = actualTheme === 'light'
    ? '/images/logos/erp.png'        // Light mode logo
    : '/images/logos/erplight.png'   // Dark mode logo

  return (
    <img 
      src={logoPath} 
      alt={alt} 
      className={className}
      style={style}
    />
  )
}

